import { DESIGN_PRESET_ENUM_VALUES, type DesignPresetProperty, type DesignPresetValue } from 'common/design-tokens';

/**
 * Where a preset cell's value comes from, in the Inspector's terms: the Base column defines the value, another column
 * either overrides it or inherits it from Base or a larger platform, and an unset cell shows nothing.
 */
export type PresetCellSource = 'base' | 'override' | 'inherited' | 'empty';

export function getPresetCellSource({
	assigned,
	inherited,
	isBase,
}: {
	assigned: DesignPresetValue | undefined;
	inherited: unknown;
	isBase: boolean;
}): PresetCellSource {
	if (assigned !== undefined) return isBase ? 'base' : 'override';
	return inherited === undefined ? 'empty' : 'inherited';
}

/** How a literal value is edited: in the cell, or in a popover for shadows */
export type PresetLiteralKind = 'number' | 'enum' | 'fontFamily' | 'color' | 'shadow';

export function getPresetLiteralKind(property: DesignPresetProperty): PresetLiteralKind {
	if (property === 'color' || property === 'backgroundColor' || property === 'borderColor') return 'color';
	if (property === 'fontFamily') return 'fontFamily';
	if (property === 'boxShadow' || property === 'textShadow') return 'shadow';
	if (property in DESIGN_PRESET_ENUM_VALUES) return 'enum';
	return 'number';
}

export type PresetNumberUnit = 'px' | 'unitless';

/** The unit a number cell writes: line height keeps the kind it has, weight and opacity have none */
export function getPresetNumberUnit(
	property: DesignPresetProperty,
	value: DesignPresetValue | undefined
): PresetNumberUnit {
	if (property === 'fontWeight' || property === 'opacity') return 'unitless';
	if (property === 'lineHeight') return typeof value === 'string' && /px$/i.test(value.trim()) ? 'px' : 'unitless';
	return 'px';
}

/** A number as the cell shows it: no unit, at most `digits` decimals; text that is not a number stays as it is */
export function formatPresetNumber(value: DesignPresetValue | undefined, digits = 2): string {
	if (value === undefined || value === '') return '';
	const number = typeof value === 'number' ? value : Number.parseFloat(value);
	if (!Number.isFinite(number)) return String(value);
	if (typeof value === 'string' && !/^-?\d*\.?\d+(px)?$/i.test(value.trim())) return value;
	return String(Number(number.toFixed(digits)));
}

/** The value to store for typed text, or undefined when it holds no number */
export function parsePresetNumberInput(text: string, unit: PresetNumberUnit): DesignPresetValue | undefined {
	const number = Number.parseFloat(text.trim().replace(',', '.'));
	if (!Number.isFinite(number)) return undefined;
	return unit === 'px' ? `${number}px` : number;
}

/** Arrow-key step for a number cell */
export function getPresetNumberStep(property: DesignPresetProperty, unit: PresetNumberUnit): number {
	if (property === 'fontWeight') return 100;
	if (property === 'opacity' || (property === 'lineHeight' && unit === 'unitless')) return 0.1;
	return 1;
}
